import {match, Pattern} from 'ts-pattern';

import type {AdvertisementPreflightIssue} from '@dms/api/sales';
import i18n from '@dms/i18n';

import {getSafely} from 'shared/web';

const SOURCE_PATH_LABELS: Record<string, string> = {
  images: i18n.t('entity.photo.labels.photos'),
  'vehicleData.vin': i18n.t('entity.vehicle.labels.vin'),
  'vehicleData.registrationPlate': i18n.t('entity.vehicle.labels.licensePlate'),
  'vehicleData.condition': i18n.t('entity.vehicle.labels.condition'),
  'vehicleData.isVatDeductible': i18n.t('general.labels.vatDeductible'),
  'vehicleData.manufacturedOn': i18n.t('entity.vehicle.labels.year'),
  'vehicleData.mileage': i18n.t('entity.vehicle.labels.mileage'),
  'vehicleData.firstRegistration': i18n.t('entity.vehicle.labels.firstRegistration'),
  'vehicleData.firstRegistrationOn': i18n.t('entity.vehicle.labels.firstRegistration'),
  'vehicleData.exteriorColor.code': i18n.t('entity.vehicle.labels.bodyColor'),
  'vehicleData.exteriorColor.value': i18n.t('entity.vehicle.labels.bodyColor'),
  'vehicleData.exteriorColorSpecification': i18n.t('entity.vehicle.labels.bodyColorType'),
  'vehicleData.originCountry': i18n.t('entity.vehicle.labels.countryOfOrigin'),
  'vehicleData.technicalInspectionValidUntil': i18n.t('entity.vehicle.labels.technicalInspection'),
  'vehicleData.lastServiceDate': i18n.t(
    'entity.vehicle.additionalInformation.labels.lastServiceDate'
  ),
  'vehicleData.lastServiceMileage': i18n.t(
    'entity.vehicle.additionalInformation.labels.lastServiceMileage'
  ),
  'vehicleData.warrantyDate': i18n.t('entity.vehicle.labels.warrantyUntil'),
  'vehicleData.warrantyMileage': i18n.t('entity.vehicle.labels.warrantyMileageLimit'),
  'vehicleData.inspections': `${i18n.t('entity.tireSet.labels.tires')} · ${i18n.t('entity.vehicle.labels.dimensions')}`,
  equipment: i18n.t('entity.rental.labels.equipment'),
  'vehicleData.sellingPrice': i18n.t('entity.vehicle.labels.sellingPrice'),
  'price.sellingPricesWithVat.EUR': `${i18n.t('entity.vehicle.labels.sellingPrice')} (EUR)`,
  'price.sellingPricesWithVat.PLN': `${i18n.t('entity.vehicle.labels.sellingPrice')} (PLN)`,
  'financingPrice.sellingPricesWithVat.PLN': `${i18n.t('entity.vehicle.labels.price_conditional_on_financing')} (PLN)`,
  'automarketpl.inspections': i18n.t('module.inspection.title'),
  'automarketpl.inspekcja': i18n.t('module.inspection.title'),
  'automarketpl.rozmiarOgumienia': `${i18n.t('entity.tireSet.labels.tires')} · ${i18n.t('entity.vehicle.labels.dimensions')}`,
  'automarketpl.wyposazKody': i18n.t('entity.rental.labels.equipment'),
  'automarketpl.krajPochodz': i18n.t('entity.vehicle.labels.countryOfOrigin'),
  'automarketpl.kolor': i18n.t('entity.vehicle.labels.bodyColor'),
  'automarketpl.typLakieru': i18n.t('entity.vehicle.labels.bodyColorType'),
  'automarketpl.zPolisaGwarancyjna': i18n.t('page.salesSettings.labels.warranty'),
  'automarketpl.wartoscPolisyGwarancyjnej': i18n.t('page.salesSettings.labels.warranty'),
  'automarketpl.badanieData': i18n.t('entity.vehicle.labels.technicalInspection'),
  'automarketpl.badaniePrzebieg': `${i18n.t('entity.vehicle.labels.technicalInspection')} — ${i18n.t('entity.vehicle.labels.mileage')}`,
  'automarketpl.przegladData': i18n.t(
    'entity.vehicle.additionalInformation.labels.lastServiceDate'
  ),
  'automarketpl.przegladPrzebieg': i18n.t(
    'entity.vehicle.additionalInformation.labels.lastServiceMileage'
  ),
  'automarketpl.historiaSerwisowa': i18n.t('feature.customerInsights.serviceHistory'),
  'automarketpl.zdjecieLewyPrzod': i18n.t('entity.photoChecker.labels.photoType.FRONT_LEFT'),
  'automarketpl.zdjecieLewyBok': i18n.t('entity.photoChecker.labels.photoType.LEFT_SIDE'),
  'automarketpl.zdjecieLewyTyl': i18n.t('entity.photoChecker.labels.photoType.REAR_LEFT'),
  'automarketpl.zdjeciePrawyTyl': i18n.t('entity.photoChecker.labels.photoType.REAR_RIGHT'),
  'automarketpl.zdjeciePrawyPrzod': i18n.t('entity.photoChecker.labels.photoType.FRONT_RIGHT'),
  'automarketpl.zdjecieOtwartyBagaznik': i18n.t('entity.photoChecker.labels.photoType.TRUNK'),
  'automarketpl.zdjecieSrodek': i18n.t('entity.inspection.section.INTERIOR'),
  'automarketpl.zdjecieTylnejKanapy': i18n.t('entity.photoChecker.labels.photoType.BACK_SEATS'),
  'automarketpl.listaPozostalychZdjec': i18n.t('entity.photo.labels.photos'),
  carPassDocumentUrl: i18n.t('page.advertisement.labels.carPassReport'),
};

function fieldNames(issue: AdvertisementPreflightIssue): string {
  return issue.sourcePaths
    .map((sourcePath) => {
      const path = sourcePath.replace(/^source\./, '');
      const arrayPath = path.split('.').slice(0, 2).join('.');
      return (
        getSafely(SOURCE_PATH_LABELS, path) ??
        getSafely(SOURCE_PATH_LABELS, arrayPath) ??
        (path.startsWith('images.') ? SOURCE_PATH_LABELS.images : undefined)
      );
    })
    .filter(Boolean)
    .join(', ');
}

function getFieldIssueText(issue: AdvertisementPreflightIssue): string {
  const fields = fieldNames(issue);
  const reason = match(issue)
    .when(
      (value) => value.kind !== 'field',
      () => i18n.t('entity.workshop.labels.notAvailable')
    )
    .with(
      {reasonCode: 'inspection-data-unavailable'},
      () =>
        `${i18n.t('entity.workshop.labels.notAvailable')}. ${i18n.t('entity.documentExtraction.actions.tryAgain')}`
    )
    .with({reasonCode: 'required-source-missing'}, () => i18n.t('errors.field_is_required'))
    .with({reasonCode: 'invalid-source-format'}, () => i18n.t('errors.invalid_string.regex'))
    .otherwise(() => i18n.t('errors.custom'));
  return fields ? `${fields}: ${reason}` : reason;
}

export function getAdvertisingPreflightIssueText(issue: AdvertisementPreflightIssue): string {
  return match(issue)
    .with({targetPath: 'kodEurotax'}, () =>
      i18n.t('page.vehicleDetail.automarketplEurotax.preflightHint')
    )
    .with({reasonCode: 'missing-carpass-url'}, () =>
      i18n.t('page.advertisement.errors.missingCarPassUrl')
    )
    .with({kind: Pattern.union('equipment-taxonomy', 'vehicle-taxonomy')}, () =>
      i18n.t('page.advertisement.errors.mappingFailed')
    )
    .otherwise(getFieldIssueText);
}
